import type { StylesConfigDto } from '@builder4/shared/dto/styles';
import type { ModelOption, AiGenerationStats } from '../types';

// ════════════════════════════════════════════════════════════════════════════
// PAGE PROFILE INPUT TYPES (from PAGE_PROFILE_TOOL schema)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Brand color entry
 */
export type BrandColor = {
  hex: string;
  rgb: { r: number; g: number; b: number };
  opacity: number;
  components: string[];
  frequency: number;
};

/**
 * Brand kit with color palette
 */
export type BrandKit = {
  white?: BrandColor;
  black?: BrandColor;
  grays?: BrandColor[];
  colors?: BrandColor[];
};

/**
 * Image category from AI analysis
 */
export type ImageCategory =
  | 'product'
  | 'model'
  | 'banner'
  | 'logo'
  | 'lifestyle'
  | 'screenshot'
  | 'unknown';

/**
 * Image purpose for landing page placement
 */
export type ImagePurpose =
  | 'hero'
  | 'product'
  | 'social-proof'
  | 'lifestyle'
  | 'branding'
  | 'decorative'
  | 'informational'
  | 'cta';

/**
 * Image trait for layout suitability
 */
export type ImageTrait =
  | 'background'
  | 'product-image'
  | 'hero-image'
  | 'thumbnail'
  | 'testimonial';

/**
 * Image metadata from profiling
 */
export type ImageMetadata = {
  width?: number;
  height?: number;
  hasTransparency?: boolean;
  dominantColor?: { r: number; g: number; b: number };
  luminance?: number;
  brightness?: 'dark' | 'light' | 'neutral';
  category?: ImageCategory;
  hasText?: boolean;
  description?: string;
  purpose?: ImagePurpose;
  traits?: ImageTrait[];
};

/**
 * A profiled image with rich metadata
 */
export type ProfiledImage = {
  url: string;
  metadata?: ImageMetadata;
};

/**
 * A customer review/testimonial
 */
export type Review = {
  author: string;
  rating: number;
  text: string;
  date: string | null;
};

/**
 * Categorization info from profiling
 */
export type Categorization = {
  industry?: string;
  subcategory?: string;
  priceTier?: 'budget' | 'mid-range' | 'premium' | 'luxury';
  price?: number;
  currency?: string;
  category?: string;
};

/**
 * Audience info from profiling
 */
export type AudienceInfo = {
  description?: string;
  ageRange?: { min?: number; max?: number };
  incomeLevel?: string;
  location?: string[];
  interests?: string[];
  painPoints?: string[];
};

/**
 * Complete page profile input (from profiling system)
 */
export type PageProfileInput = {
  id?: string;
  sessionId?: string;
  title: string;
  description: string;
  goal?: string | null;
  categorization: Categorization;
  audience?: AudienceInfo;
  images: ProfiledImage[];
  testimonials: {
    reviews: Review[];
    note: string | null;
  };
  brandKit?: BrandKit;
  fileBucket?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

// ════════════════════════════════════════════════════════════════════════════
// LAYOUT PATTERNS (14 options with descriptions)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Layout pattern options for section generation
 */
export type LayoutPattern =
  | 'single-column'
  | 'two-column-left'
  | 'two-column-right'
  | 'three-column'
  | 'three-column-with-header'
  | 'four-column'
  | 'four-column-with-header'
  | 'grid-2x2'
  | 'grid-3'
  | 'asymmetric-left'
  | 'asymmetric-right'
  | 'stacked-hero'
  | 'split-screen'
  | 'cards-row'
  | 'zigzag'
  | 'alternating'
  | 'hero-image-left'
  | 'hero-image-right'
  | 'hero-full-image'
  | 'cta-image-bg';

/**
 * Layout pattern descriptions for prompt context
 */
export const LAYOUT_PATTERN_DESCRIPTIONS: Record<LayoutPattern, string> = {
  'single-column': 'Centered content in a single column with maxWidth (800px). Focused messaging.',
  'two-column-left': 'Text left (55%), image/visual right (45%). Content-first layout.',
  'two-column-right': 'Image/visual left (45%), text right (55%). Visual-first layout.',
  'three-column':
    'Single row with 3 equal columns (33% each). Section contains ONLY a <row> with 3 <column> children—no separate heading or CTA rows. Good for feature cards without section title. Use align-start for the row.',
  'three-column-with-header':
    'Header row (centered title + subtitle) followed by a row of 3 equal columns (33% each). Structure: <column> for header, then <row> with 3 <column> children. Good for features/benefits with section title. Use align-start for the row.',
  'four-column':
    'Single row with 4 equal columns (25% each). Section contains ONLY a <row> with 4 <column> children—no separate heading or CTA rows. Good for process steps, stats, or logos. Use align-start for the row.',
  'four-column-with-header':
    'Header row (centered title + subtitle) followed by a row of 4 equal columns (25% each). Structure: <column> for header, then <row> with 4 <column> children. Good for process steps or pricing with section title. Use align-start for the row.',
  'grid-2x2': 'Two rows of two columns (50% each). Four items in a balanced 2x2 grid.',
  'grid-3': 'Three centered columns (30% each) with gap spacing. Balanced horizontal layout.',
  'asymmetric-left': 'Large left column (70%), small right (30%). Main content with sidebar.',
  'asymmetric-right': 'Small left column (30%), large right (70%). Sidebar with main content.',
  'stacked-hero': 'Full-width image above, centered text below with maxWidth. Vertical layout.',
  'split-screen': 'Two 50% columns with internal padding. Dramatic equal split layout.',
  'cards-row': 'Horizontal row of fixed-width cards (280px each), centered. Uniform cards.',
  zigzag: 'Multiple rows alternating: text-left/image-right, then image-left/text-right.',
  alternating: 'Rows alternate positioning: content-left (55%)/visual-right, then flipped.',
  'hero-image-left':
    'Two columns: left column (50%) has full-height background image with dark color overlay and reduced bgImageOpacity for readability, right column (50%) has text content on the normal page background.',
  'hero-image-right':
    'Two columns: left column (50%) has text content on the normal page background, right column (50%) has full-height background image with dark color overlay and reduced bgImageOpacity for readability.',
  'hero-full-image':
    'Section has full-width background image with a dark color overlay for text readability. Use bgImageOpacity="30-40" combined with a dark bg color. All text sits on top of the darkened image.',
  'cta-image-bg':
    'CTA section with background image and dark overlay. Centered headline + CTA button on top of image. Use bgImageOpacity="30-40" combined with a dark bg color. Dramatic, conversion-focused.',
};

// ════════════════════════════════════════════════════════════════════════════
// CONTENT OUTLINE TYPES (output of plan stage)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Tone options for content generation
 */
export type ToneType =
  | 'professional'
  | 'playful'
  | 'luxury'
  | 'bold'
  | 'minimal'
  | 'warm'
  | 'technical'
  | 'friendly';

/**
 * Section type identifiers
 */
export type SectionType =
  | 'hero'
  | 'features'
  | 'benefits'
  | 'social-proof'
  | 'cta'
  | 'pricing'
  | 'faq'
  | 'team'
  | 'gallery'
  | 'contact'
  | 'stats'
  | 'process'
  | 'comparison'
  | 'logo-bar'
  | 'newsletter'
  | 'video'
  | 'quote'
  | 'about'
  | 'services'
  | 'portfolio'
  | 'awards';

/**
 * Design directives for visual styling
 */
export type DesignDirectives = {
  spacingScale: 'tight' | 'balanced' | 'airy';
  borderRadiusScale: 'sharp' | 'subtle' | 'rounded';
  shadowUsage: 'none' | 'subtle' | 'dramatic';
  layoutDensity: 'compact' | 'standard' | 'spacious';
};

/**
 * Page-level animation strategy decided during planning.
 * Keeps animation type consistent across all sections.
 */
export type AnimationStrategy = {
  sectionEntry: 'fadeIn' | 'slideUp' | 'blurIn' | 'revealUp' | 'zoomIn';
  cardHover: 'scale' | 'lift' | 'glow' | 'none';
  imageHover: 'scale' | 'brighten' | 'none';
  imageContinuous: 'kenBurns' | 'slowZoom' | 'none';
};

/**
 * LLM-assigned image reference (output from planning stage)
 */
export type AssignedImageRef = {
  imageId: string; // e.g., "img_0", "img_1"
  role: 'hero' | 'feature' | 'background' | 'accent' | 'testimonial' | 'product';
  fit: 'cover' | 'contain';
};

/**
 * LLM-assigned testimonial reference (output from planning stage)
 */
export type AssignedTestimonialRef = {
  testimonialId: string; // e.g., "review_0", "review_1"
};

/**
 * A resolved image assigned to a section (with full data)
 */
export type AssignedImage = {
  url: string;
  alt: string;
  role: 'hero' | 'feature' | 'background' | 'accent' | 'testimonial' | 'product';
  width: number;
  height: number;
  fit: 'cover' | 'contain';
  suggestedBackground?: string;
  luminance?: number;
  brightness?: 'dark' | 'light' | 'neutral';
};

/**
 * A single section entry in the content outline (as output by LLM)
 */
export type SectionOutlineEntry = {
  // Metadata
  type: SectionType;
  purpose: string;

  // Layout direction
  layoutPattern: LayoutPattern;
  spacingIntensity: 'tight' | 'balanced' | 'airy';

  // Visual direction
  visualStyle: {
    backgroundType: 'transparent' | 'light' | 'dark' | 'accent' | 'gradient';
    backgroundHint: string;
  };

  // Content direction
  contentDirection: {
    headline: string;
    subheadlineTheme: string;
    bodyTheme: string;
    ctaText?: string;
  };

  // LLM-assigned image references (imageId, role, fit)
  assignedImages?: AssignedImageRef[];

  // LLM-assigned testimonial references (testimonialId)
  assignedTestimonials?: AssignedTestimonialRef[];

  // Resolved images (populated after resolving refs)
  resolvedImages?: AssignedImage[];

  // Resolved testimonials (populated after resolving refs)
  resolvedTestimonials?: Review[];
};

/**
 * Output of Stage 1: PLAN
 * Complete content outline with section plan and assignments
 */
export type PageProfileOutline = {
  // Business context
  businessType: string;
  industry: string;
  tone: ToneType;
  pagePurpose: string;
  targetAudience: string;
  valueProposition: string;
  colorDirection: string;
  designDirectives: DesignDirectives;

  // Page-level animation strategy for consistency
  animationStrategy: AnimationStrategy;

  // Page-level config
  pageConfig: {
    backgroundColor: string;
    maxWidth: string;
    padding: string;
  };

  // Section outline
  sections: SectionOutlineEntry[];
};

// ════════════════════════════════════════════════════════════════════════════
// SECTION GENERATION TYPES
// ════════════════════════════════════════════════════════════════════════════

/**
 * A generated section from the generation stage
 */
export type GeneratedSection = {
  xml: string;
  index: number;
  summary: string;
  layoutPattern: string;
};

// ════════════════════════════════════════════════════════════════════════════
// ORCHESTRATOR TYPES
// ════════════════════════════════════════════════════════════════════════════

/**
 * Progress phases for the page profile orchestrator UI
 */
export type PageProfilePhase = 'idle' | 'planning' | 'styling' | 'generating-sections' | 'complete';

/**
 * Progress event emitted by the orchestrator
 */
export type PageProfileProgress = {
  phase: PageProfilePhase;
  message: string;
  sectionIndex?: number;
  totalSections?: number;
};

/**
 * Callbacks for the page profile orchestrator
 */
export type PageProfileCallbacks = {
  onProgress: (progress: PageProfileProgress) => void;
  onPlanComplete: (outline: PageProfileOutline) => void;
  onStyleGenerated: (style: StylesConfigDto, label: string) => void;
  onSectionComplete: (section: GeneratedSection) => void;
  onComplete: () => void;
  onError: (error: Error) => void;
};

/**
 * Options for the page profile orchestrator
 */
export type PageProfileOptions = {
  input: PageProfileInput;
  existingStyles: StylesConfigDto;
  signal?: AbortSignal;
  model?: ModelOption;
  stats?: AiGenerationStats;
};
